import { Injectable, Type, inject, signal } from '@angular/core';

import { NgpDialogManager, NgpDialogRef } from 'ng-primitives/dialog';

import { SuiDialogConfig } from './dialog-config';
import { SuiDialogRef } from './sui-dialog-ref';
import { ZIndex } from '../../app/core/constants';

/*
 * SUI Dialog Service
 *
 * Provides typed wrappers around NgpDialogManager for different dialog variants.
 * Each method opens the business component directly, which then uses the appropriate
 * SUI layout wrapper (e.g., <sui-dialog-tabs>) in its template via slot projection.
 *
 * Architecture:
 * 1. DialogService.openTabsDialog(BusinessComponent, { data })
 * 2. Manager opens BusinessComponent, wrapped in SuiDialogRef
 * 3. BusinessComponent template uses <sui-dialog-tabs> with slot projection
 * 4. BusinessComponent injects SuiDialogRef to access data and close the dialog
 *
 * Features:
 * - Automatic z-index management for sequential dialog stacking
 * - Cleanup tracking for proper z-index recalculation
 * - Standardized dialog result handling via SuiDialogRef
 * - Type-safe dialog input/output data
 */
@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private dialogManager = inject(NgpDialogManager);

  // * Track open dialogs for z-index management
  private openDialogs: NgpDialogRef[] = [];

  /**
   * Number of open dialogs. Read by the root template (WAP-1718): a dialog
   * overlay sits beside the OnPush root, so a click in it marks nothing on the
   * route path; this signal makes every open and close repaint the page once,
   * so state a screen sets after `await dialogRef.closed` renders on arrival.
   */
  readonly openCount = signal(0);
  private readonly zIndexIncrement = 10;

  openTabsDialog<T, R = unknown>(
    businessComponent: Type<T>,
    config?: SuiDialogConfig<unknown>,
  ): SuiDialogRef<unknown, R> {
    return this.openDialog(businessComponent, config);
  }

  openSimpleDialog<T, R = unknown>(
    businessComponent: Type<T>,
    config?: SuiDialogConfig<unknown>,
  ): SuiDialogRef<unknown, R> {
    return this.openDialog(businessComponent, config);
  }

  openConfirmDialog<T, R = unknown>(
    businessComponent: Type<T>,
    config?: SuiDialogConfig<unknown>,
  ): SuiDialogRef<unknown, R> {
    return this.openDialog(businessComponent, config);
  }

  openDrawer<T, R = unknown>(
    businessComponent: Type<T>,
    config?: SuiDialogConfig<unknown>,
  ): SuiDialogRef<unknown, R> {
    return this.openDialog(businessComponent, config);
  }

  // * internal method to open dialog with automatic z-index management
  private openDialog<T, R = unknown>(
    businessComponent: Type<T>,
    config?: SuiDialogConfig<unknown>,
  ): SuiDialogRef<unknown, R> {
    const zIndex = this.calculateZIndex();

    const configWithZIndex: SuiDialogConfig<unknown> = {
      ...config,
      zIndex,
    };

    const ngpRef = this.dialogManager.open<unknown, R>(
      businessComponent,
      configWithZIndex,
    );

    // * wrap NgpDialogRef in SuiDialogRef for standardized API
    // * Component will inject NgpDialogRef and wrap it internally via SuiDialogBase
    const suiRef = new SuiDialogRef<unknown, R>(ngpRef);

    this.trackDialog(ngpRef);

    return suiRef;
  }

  // * calculate z-index based on currently open dialogs
  private calculateZIndex(): number {
    return ZIndex.DIALOG + this.openDialogs.length * this.zIndexIncrement;
  }

  // * track dialog and clean up when closed
  private trackDialog(ref: NgpDialogRef): void {
    this.openDialogs.push(ref);
    this.openCount.set(this.openDialogs.length);

    ref.closed.subscribe(() => {
      const index = this.openDialogs.indexOf(ref);
      if (index > -1) {
        this.openDialogs.splice(index, 1);
      }
      this.openCount.set(this.openDialogs.length);
    });
  }

  getOpenDialogCount(): number {
    return this.openCount();
  }

  closeAll(): void {
    [...this.openDialogs].forEach((ref) => ref.close());
  }
}
